import { TileArtwork } from "@/components/grow/TileArtwork";
import { tileHueFromId } from "@/lib/questionnaire-group";

export interface ToplistItem {
  id: string;
  title: string;
  // A short fact under the title, e.g. the release year.
  subtitle?: string;
  labels?: readonly string[];
  // The colour of the entry's first category; without one the card derives its own.
  hue?: number;
}

const SHELL = "box relative flex w-full flex-col overflow-hidden bg-surface text-left";

// One catalogue entry as a compact card, in the shape of the Grow · Play tiles,
// made for grids of four or more (two columns on a phone): the title, and year
// and labels on one quiet line. There is no poster and no generated pattern
// either (a grid of lines read as a picture that was not there): a plain strip
// in the colour of the entry's first category, so a category reads at a glance.
// An entry without one takes a hue from its id and keeps it everywhere in the game.
//
// With `onClick` the whole card is a button. With `selected` as well it is a
// toggle button (aria-pressed), marked by a ring and a check.
export function ToplistCard({
  item,
  onClick,
  selected,
  disabled = false,
}: {
  item: ToplistItem;
  onClick?: () => void;
  selected?: boolean;
  disabled?: boolean;
}) {
  const labels = [...new Set(item.labels ?? [])];
  const body = (
    <>
      <TileArtwork hue={item.hue ?? tileHueFromId(item.id)} variant="plain" className="h-2 w-full" />
      {/* pr-8 keeps the title clear of the check of a selected card. */}
      <span className="flex flex-col py-2 pl-3 pr-8">
        <span className="text-smycles-body font-medium leading-snug">{item.title}</span>
        {item.subtitle || labels.length > 0 ? (
          <span className="mt-1 text-smycles-label text-ink-muted">{[item.subtitle, ...labels].filter(Boolean).join(" · ")}</span>
        ) : null}
      </span>
      {selected ? (
        <span
          aria-hidden="true"
          className="absolute right-2 top-3 flex h-5 w-5 items-center justify-center rounded-full bg-ink text-smycles-label text-ink"
        >
          ✓
        </span>
      ) : null}
    </>
  );

  if (!onClick) return <article className={SHELL}>{body}</article>;
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-pressed={selected}
      className={`${SHELL} transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ink/30 disabled:cursor-default disabled:opacity-50 ${
        selected ? "ring-2 ring-ink" : "hover:ring-ink/15"
      }`}
    >
      {body}
    </button>
  );
}
